<template>
<div class="chapter">
<div class="inner_page">
<a id="page_6"></a>
<h2 class="ct-1">Knitting know-how</h2>
<p class="noindent-1"><span class="bold">Multicoloured techniques</span></p>
<p class="noindent-a"><span class="bold">Fair Isle</span> Make sure the strands at the back of the work are pulled tightly enough to stop little fingers getting caught, but not too tight to pull in the work.</p>
<p class="noindent-a"><span class="bold">Intarsia</span> Before starting, wind off small amounts of yarn for each area of colour in the motif. Twist the yarn at the back of the work when changing colour.</p>
<p class="noindent-1"><span class="bold">Magic loop method</span></p>
<p class="noindent-a">The magic loop is a good technique to use when you have a small number of stitches and a small circumference to knit on circular needles. Take care to not get the stitches twisted around the needles at any point.</p>
<p class="noindent-a"><span class="bold">1</span> Cast on the required number of stitches onto your circular needle.</p>
<p class="noindent-a"><span class="bold">2</span> Move all the stitches to the centre of the flexible part of the needle (the cable), then find the approximate centre point for the stitches.</p>
<p class="noindent-a"><span class="bold">3</span> Divide the work into two, then gently push the two sets of stitches apart and back onto the needles, creating a loop shape on the cable. This should be on your left. Make sure that the stitches do not twist – this is very important, as if they do you will need to begin again.</p>
<p class="noindent-a"><span class="bold">4</span> Hold the needles parallel to your body, with one in front of the other, so that the needle with the stitches first cast on is closest to you. Put the working yarn over the back needle if the first stitch is a k stitch, or between the needles nearest to you if it is a p stitch.</p>
<p class="noindent-a"><span class="bold">5</span> Push the stitches on the back needle back onto the cable. Do this by gripping the point of the needle, and gently pulling it to the right then forward. Position your needle in the first stitch ready to knit. You should now have a loop of cable also on your right.</p>
<p class="noindent-a"><span class="bold">6</span> Begin to knit the stitches, knitting across the first needle.</p>
<p class="noindent-a"><span class="bold">7</span> To turn the work, turn the needles so that the unworked stitches are now nearest to you. Pull the stitches at the back onto the cable needle in the same way as before <span class="bold">(5).</span> These are the ones just worked. Keeping the working yarn towards the back, bring the back needle around and prepare to k the first stitch.</p>
<p class="noindent-a"><span class="bold">8</span> Once this second set of stitches have been worked, one round has been completed. It is useful to place a marker here, so that are easily able to recognise he beginning of a new round. Continue until the number of rounds have been worked according to the pattern.</p>
<p class="noindent-1"><span class="bold">Size</span></p>
<p class="noindent-a">The three sizes have been laid out in the patterns with the smallest first and the larger two following in brackets: 0–3(3–6:6–12) months.</p>
<p class="noindent-1"><span class="bold">Tension</span></p>
<p class="noindent-a">As the mittens need to be a good fit, the finished measurements are important. If you can’t get the correct tension, change the needle size until you do.</p>
<p class="noindent-1"><span class="bold">Sewing up</span></p>
<p class="noindent-a">Break yarn, leaving long tails to thread through remaining stitches with a tapestry (blunt-end) needle. Pull top of mittens tight and weave in the ends.</p>
<p class="noindent-1"><span class="bold">Making a plaited tie</span></p>
<p class="noindent-a">Cut the lengths of yarn as the pattern suggests. Tie a knot in one end and plait to form a tie. Secure at the end with a second knot and trim the ends of the strands of yarn.</p>
<div class="img_container">
<img alt="images" src="images/f008-01.jpg"/>
</div>
<a id="page_7"></a>
<div class="box-abb">
<p class="noindent-1"><span class="bold">Abbreviations</span></p>
<p class="noindent-a">The abbreviations listed below are the most frequently used terms in the book. Any special abbreviations in a pattern are explained on the relevant page.</p>
<table>
<tbody>
<tr>
<td class="tab"><span class="bold">DPN</span></td>
<td class="tab">double-pointed needles</td>
</tr>
<tr>
<td class="tab"><span class="bold">k</span></td>
<td class="tab">knit</td>
</tr>
<tr>
<td class="tab"><span class="bold">k2tog</span></td>
<td class="tab">knit 2 stitches together</td>
</tr>
<tr>
<td class="tab"><span class="bold">k2togtbl</span></td>
<td class="tab">knit 2 stitches together through back loop</td>
</tr>
<tr>
<td class="tab"><span class="bold">m1</span></td>
<td class="tab">make 1 stitch by lifting bar of yarn before next stitch with the left needle, and then knitting into the back of loop</td>
</tr>
<tr>
<td class="tab"><span class="bold">p</span></td>
<td class="tab">purl</td>
</tr>
<tr>
<td class="tab"><span class="bold">patt</span></td>
<td class="tab">pattern</td>
</tr>
<tr>
<td class="tab"><span class="bold">p2tog</span></td>
<td class="tab">purl 2 stitches together</td>
</tr>
<tr>
<td class="tab"><span class="bold">psso</span></td>
<td class="tab">pass slip stitch over</td>
</tr>
<tr>
<td class="tab"><span class="bold">rep</span></td>
<td class="tab">repeat</td>
</tr>
<tr>
<td class="tab"><span class="bold">ssk</span></td>
<td class="tab">slip 2 stitches knitwise one at a time</td>
</tr>
<tr>
<td class="tab"><span class="bold">sl</span></td>
<td class="tab">slip stitch</td>
</tr>
<tr>
<td class="tab"><span class="bold">sm</span></td>
<td class="tab">slip marker</td>
</tr>
<tr>
<td class="tab"><span class="bold">st</span></td>
<td class="tab">stitch</td>
</tr>
<tr>
<td class="tab"><span class="bold">st st</span></td>
<td class="tab">stocking/stockinette stitch</td>
</tr>
<tr>
<td class="tab"><span class="bold">tbl</span></td>
<td class="tab">through back of loop</td>
</tr>
<tr>
<td class="tab"><span class="bold">yo</span></td>
<td class="tab">yarn over</td>
</tr>
</tbody>
</table>
</div>
<div class="img_container">
<img alt="images" src="images/f009-01.jpg"/>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 06_Chapter01.vue?17aec16c